// TS twin of iOS `ModelDownloader.swift`.
//
// Fetches the wheel model binary (USDZ on iOS, GLB on Android WebAR) into an
// in-memory ArrayBuffer that Three.js GLTFLoader consumes directly.
//
// Closes the audit §7 MEDIUM finding two ways:
// 1. Content-Length pre-flight: reject before reading any body bytes when
//    the server-declared size exceeds the ceiling.
// 2. Streaming running-total check: reject mid-stream if cumulative bytes
//    cross the ceiling — defends against servers that omit / lie about
//    Content-Length.
//
// fetch is injectable via constructor — production passes the platform
// fetch; tests pass in-memory scripted Response objects.
//
// Concurrent downloads do NOT share state (unlike the iOS single-slot
// continuation bug the audit §1 HIGH calls out): each download() call is
// its own promise chain with its own chunks accumulator.

import { atoLog } from '@/services/ato-log'

export type ModelDownloaderErrorKind =
  | 'invalid_url'
  | 'network_error'
  | 'invalid_response'
  | 'too_large'

interface ModelDownloaderErrorOpts {
  message: string
  httpStatus?: number
  sizeBytes?: number
  maxBytes?: number
  cause?: unknown
}

export class ModelDownloaderError extends Error {
  readonly kind: ModelDownloaderErrorKind
  readonly httpStatus: number | undefined
  readonly sizeBytes: number | undefined
  readonly maxBytes: number | undefined

  constructor(kind: ModelDownloaderErrorKind, opts: ModelDownloaderErrorOpts) {
    super(opts.message, { cause: opts.cause })
    this.name = 'ModelDownloaderError'
    this.kind = kind
    this.httpStatus = opts.httpStatus
    this.sizeBytes = opts.sizeBytes
    this.maxBytes = opts.maxBytes
  }

  get category(): string {
    return this.kind
  }
}

// Matches the audit recommendation: 30 MiB, well under the App Clip ~50 MB
// compressed runtime budget so a USDZ that's marginally too big still
// fails closed before OOM-killing the clip.
const DEFAULT_MAX_BYTES = 30 * 1024 * 1024

export interface ModelDownloaderConfig {
  fetch?: typeof fetch
  maxBytes?: number
}

export interface DownloadOptions {
  url: URL
  signal?: AbortSignal
  /** Fraction received, 0–1, per chunk — only when the server declares a
   *  Content-Length (?use_ios_ui download card, 2026-10-17). */
  onProgress?: (fraction: number) => void
}

export interface DownloadResult {
  readonly bytes: ArrayBuffer
  readonly contentType: string | null
}

export class ModelDownloader {
  private readonly fetchImpl: typeof fetch
  private readonly maxBytes: number

  constructor(config: ModelDownloaderConfig = {}) {
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis)
    this.maxBytes = config.maxBytes ?? DEFAULT_MAX_BYTES
  }

  async download(opts: DownloadOptions): Promise<DownloadResult> {
    const startTime = Date.now()
    const { url, signal, onProgress } = opts

    atoLog.debug(`⬇️  download start url=${url.toString()}`)

    let response: Response
    try {
      throwIfAborted(signal)
      response = await this.fetchImpl(url, { signal })
    } catch (e) {
      if (isAbortError(e)) {
        this.emitAborted(url, 0, startTime)
        throw e
      }
      const err = new ModelDownloaderError('network_error', {
        message: String((e as Error)?.message ?? e),
        cause: e,
      })
      this.emitFail(err, url, startTime)
      throw err
    }

    if (response.status !== 200) {
      const err = new ModelDownloaderError('invalid_response', {
        message: `Invalid response from server (status: ${response.status}).`,
        httpStatus: response.status,
      })
      this.emitFail(err, url, startTime)
      // Release the body so the connection can be reused.
      void response.body?.cancel().catch(() => undefined)
      throw err
    }

    // Pre-flight: server-declared Content-Length.
    const contentLengthHeader = response.headers.get('Content-Length')
    const declaredLength =
      contentLengthHeader != null ? Number(contentLengthHeader) : NaN
    if (Number.isFinite(declaredLength) && declaredLength > this.maxBytes) {
      const err = new ModelDownloaderError('too_large', {
        message: `Model exceeds size ceiling (${declaredLength} > ${this.maxBytes}).`,
        sizeBytes: declaredLength,
        maxBytes: this.maxBytes,
      })
      this.emitFail(err, url, startTime)
      void response.body?.cancel().catch(() => undefined)
      throw err
    }

    const contentType = response.headers.get('Content-Type')

    // Stream chunks while enforcing the running ceiling. Falls back to
    // arrayBuffer() when the runtime can't expose a ReadableStream.
    const reader = response.body?.getReader()
    if (reader == null) {
      const buf = await response.arrayBuffer()
      if (buf.byteLength > this.maxBytes) {
        const err = new ModelDownloaderError('too_large', {
          message: `Model exceeds size ceiling (${buf.byteLength} > ${this.maxBytes}).`,
          sizeBytes: buf.byteLength,
          maxBytes: this.maxBytes,
        })
        this.emitFail(err, url, startTime)
        throw err
      }
      this.emitSuccess(buf.byteLength, url, startTime)
      return { bytes: buf, contentType }
    }

    const chunks: Uint8Array[] = []
    let total = 0
    try {
      while (true) {
        throwIfAborted(signal)
        const { done, value } = await reader.read()
        if (done) break
        if (value == null) continue
        total += value.byteLength
        if (total > this.maxBytes) {
          const err = new ModelDownloaderError('too_large', {
            message: `Model exceeds size ceiling (${total} > ${this.maxBytes}).`,
            sizeBytes: total,
            maxBytes: this.maxBytes,
          })
          await reader.cancel(err).catch(() => undefined)
          this.emitFail(err, url, startTime)
          throw err
        }
        chunks.push(value)
        if (Number.isFinite(declaredLength) && declaredLength > 0) {
          onProgress?.(Math.min(1, total / declaredLength))
        }
      }
    } catch (e) {
      if (e instanceof ModelDownloaderError) throw e
      await reader.cancel(e).catch(() => undefined)
      if (isAbortError(e)) {
        this.emitAborted(url, total, startTime)
        throw e
      }
      const err = new ModelDownloaderError('network_error', {
        message: String((e as Error)?.message ?? e),
        sizeBytes: total,
        cause: e,
      })
      this.emitFail(err, url, startTime)
      throw err
    }

    // Concat chunks into one ArrayBuffer.
    const out = new Uint8Array(total)
    let offset = 0
    for (const chunk of chunks) {
      out.set(chunk, offset)
      offset += chunk.byteLength
    }
    this.emitSuccess(total, url, startTime)
    return { bytes: out.buffer, contentType }
  }

  private emitSuccess(bytes: number, url: URL, startTime: number): void {
    atoLog.event('model_download_completed', {
      url: url.toString(),
      bytes_downloaded: bytes,
      duration_s: (Date.now() - startTime) / 1000,
    })
  }

  private emitFail(
    err: ModelDownloaderError,
    url: URL,
    startTime: number,
  ): void {
    const props: Record<string, unknown> = {
      url: url.toString(),
      duration_s: (Date.now() - startTime) / 1000,
      error_type: err.category,
    }
    if (err.httpStatus != null) props.http_status = err.httpStatus
    if (err.sizeBytes != null) props.bytes_downloaded = err.sizeBytes
    if (err.maxBytes != null) props.max_bytes = err.maxBytes
    Object.assign(props, atoLog.errorProperties(err))
    atoLog.event('model_download_failed', props)
  }

  private emitAborted(url: URL, bytes: number, startTime: number): void {
    atoLog.event('model_download_failed', {
      url: url.toString(),
      bytes_downloaded: bytes,
      duration_s: (Date.now() - startTime) / 1000,
      error_type: 'aborted',
    })
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}
